/**
 * MarketingConsentManager.js
 * ----------------------------------------------------------------------------
 * ADD-ON component for the Admin Dashboard. Self-contained: pick a vendor,
 * see that vendor's customers and their marketing-comms subscription state,
 * toggle any customer on/off.
 *
 * Wiring into AdminDashboard.js is 3 small additions (see integration guide):
 *   1. import this component
 *   2. one <button> in the <nav>
 *   3. one line in <main>:  {activeTab === 'marketing' && <MarketingConsentManager />}
 *
 * No existing admin code is modified.
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from '../utils/axiosConfig';

function MarketingConsentManager() {
  const [vendors, setVendors] = useState([]);
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [customers, setCustomers] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState(null);

  // Load vendor list once.
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get('/api/admin/marketing-consent/vendors');
        setVendors(res.data);
      } catch (err) {
        console.error('Error loading vendors:', err);
        setMessage({ type: 'error', text: 'Failed to load vendors' });
      } finally {
        setLoadingVendors(false);
      }
    })();
  }, []);

  // Load customers whenever a vendor is chosen.
  const loadCustomers = useCallback(async (vendorId) => {
    if (!vendorId) { setCustomers([]); return; }
    setLoadingCustomers(true);
    try {
      const res = await axios.get(
        `/api/admin/marketing-consent/vendors/${vendorId}/customers`);
      setCustomers(res.data);
    } catch (err) {
      console.error('Error loading customers:', err);
      setMessage({ type: 'error', text: 'Failed to load customers' });
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  const handleVendorChange = (e) => {
    const id = e.target.value;
    setSelectedVendorId(id);
    setSearch('');
    loadCustomers(id);
  };

  // Toggle one customer's consent.
  const handleToggle = async (customer) => {
    const next = !customer.marketing_consent;
    setSavingId(customer.id);
    try {
      await axios.put(
        `/api/admin/marketing-consent/customers/${customer.id}`,
        { marketing_consent: next });
      setCustomers(prev => prev.map(c =>
        c.id === customer.id ? { ...c, marketing_consent: next } : c));
      setMessage({
        type: 'success',
        text: `${customer.full_name} ${next ? 'subscribed' : 'unsubscribed'}`,
      });
    } catch (err) {
      console.error('Error updating consent:', err);
      setMessage({ type: 'error', text: 'Failed to update consent' });
    } finally {
      setSavingId(null);
    }
  };

  // Auto-clear the status message.
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 3000);
    return () => clearTimeout(t);
  }, [message]);

  const filteredCustomers = customers.filter(c => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (c.full_name || '').toLowerCase().includes(q)
        || (c.email || '').toLowerCase().includes(q);
  });

  const consentedCount = customers.filter(c => c.marketing_consent).length;

  return (
    <div className="mcm-container">
      <h2 className="mcm-title">Marketing Communications</h2>
      <p className="mcm-subtitle">
        View and manage which customers are subscribed to marketing emails,
        per vendor.
      </p>

      {message && (
        <div className={`mcm-message mcm-message--${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="mcm-controls">
        <label className="mcm-label" htmlFor="mcm-vendor">Vendor</label>
        <select
          id="mcm-vendor"
          className="mcm-select"
          value={selectedVendorId}
          onChange={handleVendorChange}
          disabled={loadingVendors}
        >
          <option value="">
            {loadingVendors ? 'Loading vendors...' : 'Select a vendor'}
          </option>
          {vendors.map(v => (
            <option key={v.id} value={v.id}>
              {v.business_name} ({v.consented_customers}/{v.total_customers} subscribed)
            </option>
          ))}
        </select>
      </div>

      {selectedVendorId && (
        <>
          <div className="mcm-controls">
            <input
              className="mcm-search"
              type="text"
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="mcm-count">
              {consentedCount} of {customers.length} subscribed
            </span>
          </div>

          {loadingCustomers ? (
            <p className="mcm-empty">Loading customers...</p>
          ) : filteredCustomers.length === 0 ? (
            <p className="mcm-empty">No customers found.</p>
          ) : (
            <table className="mcm-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredCustomers.map(c => (
                  <tr key={c.id}>
                    <td>{c.full_name}</td>
                    <td>{c.email}</td>
                    <td>
                      <span className={`mcm-badge ${c.marketing_consent
                        ? 'mcm-badge--on' : 'mcm-badge--off'}`}>
                        {c.marketing_consent ? 'Subscribed' : 'Not subscribed'}
                      </span>
                    </td>
                    <td>
                      <button
                        className={`mcm-toggle-btn ${c.marketing_consent
                          ? 'mcm-toggle-btn--unsub' : 'mcm-toggle-btn--sub'}`}
                        disabled={savingId === c.id}
                        onClick={() => handleToggle(c)}
                      >
                        {savingId === c.id
                          ? '...'
                          : c.marketing_consent ? 'Unsubscribe' : 'Subscribe'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      <style>{`
        .mcm-container { padding: 8px 4px; }
        .mcm-title { margin: 0 0 4px; }
        .mcm-subtitle { color: #666; margin: 0 0 16px; font-size: 0.9em; }
        .mcm-controls {
          display: flex; align-items: center; gap: 12px;
          margin-bottom: 16px; flex-wrap: wrap;
        }
        .mcm-label { font-weight: 600; }
        .mcm-select, .mcm-search {
          padding: 8px 10px; border: 1px solid #ccc;
          border-radius: 6px; font-size: 0.95em; min-width: 240px;
        }
        .mcm-count { color: #666; font-size: 0.9em; }
        .mcm-table {
          width: 100%; border-collapse: collapse; background: #fff;
        }
        .mcm-table th, .mcm-table td {
          text-align: left; padding: 10px 12px;
          border-bottom: 1px solid #eee;
        }
        .mcm-table th { font-size: 0.85em; color: #555; }
        .mcm-badge {
          padding: 3px 10px; border-radius: 999px; font-size: 0.8em;
        }
        .mcm-badge--on { background: #e6f4ea; color: #1e7e34; }
        .mcm-badge--off { background: #f1f1f1; color: #777; }
        .mcm-toggle-btn {
          padding: 6px 14px; border: none; border-radius: 6px;
          cursor: pointer; font-size: 0.85em; color: #fff;
        }
        .mcm-toggle-btn--sub { background: #2e7d32; }
        .mcm-toggle-btn--unsub { background: #c62828; }
        .mcm-toggle-btn:disabled { opacity: 0.6; cursor: default; }
        .mcm-empty { color: #888; padding: 16px 0; }
        .mcm-message {
          padding: 10px 14px; border-radius: 6px; margin-bottom: 12px;
        }
        .mcm-message--success { background: #e6f4ea; color: #1e7e34; }
        .mcm-message--error { background: #fdecea; color: #c62828; }
      `}</style>
    </div>
  );
}

export default MarketingConsentManager;